import { type CSSProperties, type FocusEvent, type RefObject, useCallback, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { getFabShellTransitionStyle } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentSpaceMotion';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

const FAB_SHELL_TRANSITION_STYLE = getFabShellTransitionStyle();

export type UseWorkspaceAgentIdleFabResult = {
  buttonRef: RefObject<HTMLButtonElement>;
  isExpanded: boolean;
  ctaTransitionStyle: CSSProperties;
  handleBlur: () => void;
  handleFocus: (event: FocusEvent<HTMLButtonElement>) => void;
  handlePointerEnter: () => void;
  handlePointerLeave: () => void;
};

/**
 * Hover/focus expand and minimize-focus rescue for the pre-ui2026 idle FAB.
 * Morph-into-bar is not restored — the FAB and selection bar swap as separate phases.
 */
export const useWorkspaceAgentIdleFab = (): UseWorkspaceAgentIdleFabResult => {
  const [isHovered, setIsHovered] = useState(false);
  const [isFocusVisible, setIsFocusVisible] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pendingFocus = useWorkspaceAgentPanelStore((state) => state.pendingFocus);
  const clearPendingFocus = useWorkspaceAgentPanelStore((state) => state.clearPendingFocus);

  const handlePointerEnter = useCallback(() => setIsHovered(true), []);
  const handlePointerLeave = useCallback(() => setIsHovered(false), []);
  const handleBlur = useCallback(() => setIsFocusVisible(false), []);
  const handleFocus = useCallback((event: FocusEvent<HTMLButtonElement>) => {
    setIsFocusVisible(event.currentTarget.matches(':focus-visible'));
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (pendingFocus !== 'fab' || !buttonRef.current) {
      return;
    }

    buttonRef.current.focus();
    clearPendingFocus();
  }, [clearPendingFocus, pendingFocus]);

  return {
    buttonRef,
    isExpanded: isHovered || isFocusVisible,
    ctaTransitionStyle: FAB_SHELL_TRANSITION_STYLE,
    handleBlur,
    handleFocus,
    handlePointerEnter,
    handlePointerLeave,
  };
};
